/**
 * Restaurant table and zone shapes as returned by restaurant-service through
 * the gateway (`/v1/restaurants/tables`, `/v1/restaurants/zones`).
 *
 * Floor-plan coordinates are integer grid cells, never pixels: each zone
 * declares a `columns x rows` grid and tables are placed on it. Clients scale
 * one cell to whatever pixel size fits the screen. See fnf-docs → Backend →
 * Table Floor Plan for the contract shared with the waiter app.
 */

export interface TableLayout {
  /** Top-left cell of the table's bounding box. */
  x: number;
  y: number;
  /** Size in cells. Absent = derive from shape + seat count. */
  width?: number | null;
  height?: number | null;
  /** Whole degrees, 0..359, clockwise about the box centre. */
  rotation?: number;
}

/** A grid corner (not a cell): x runs 0..columns, y runs 0..rows. */
export interface GridPoint {
  x: number;
  y: number;
}

export interface ZoneLayout {
  columns: number;
  rows: number;
  /**
   * Polygon of grid corners tracing the zone's footprint (L-shapes, alcoves).
   * Absent/null = the whole rectangle. 3..64 points, all within the grid.
   */
  outline?: GridPoint[] | null;
}

/** Applied when a zone has no `layout` yet. Must match the backend default. */
export const DEFAULT_ZONE_LAYOUT: Readonly<ZoneLayout> = { columns: 40, rows: 24 };

export interface TableZone {
  id: string;
  zone_name: string;
  capacity?: number | null;
  wheelchair_accessibility?: boolean | null;
  floor?: number | null;
  location_types?: string[] | null;
  layout?: ZoneLayout | null;
}

export type TableStatus = 'available' | 'occupied' | 'reserved' | string;

export interface Table {
  table_id: string;
  restaurant_id: string;
  table_name: string;
  number_of_seats: number;
  status: TableStatus;
  shape: string;
  zone?: TableZone | null;
  occupied_start_time?: string | null;
  qr_code_path?: string | null;
  current_order_id?: string | null;
  current_server_id?: string | null;
  current_server?: { first_name?: string; last_name?: string } | null;
  version?: number | null;
  /** Null/absent = not placed on the floor plan yet. */
  layout?: TableLayout | null;
}

/** One entry of `PUT /v1/restaurants/tables/layout`. `layout: null` unplaces. */
export interface TableLayoutItem {
  table_id: string;
  layout: TableLayout | null;
  version?: number | null;
}

export interface BulkTableLayoutUpdate {
  tables: TableLayoutItem[];
}

/**
 * Tables a restaurant pushes together for a party no single table seats, and
 * the party sizes that works for (FNF-1182). Bookings use one only when no
 * single table fits.
 */
export interface TableCombination {
  combination_id: string;
  table_ids: string[];
  min_party: number;
  max_party: number;
  name?: string | null;
}

export type TableCombinationInput = Omit<TableCombination, 'combination_id'>;
